'use client';
import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

export function usePostHogFeatureFlag(
  flagName: string,
  { waitForLoad = false }: { waitForLoad?: boolean } = {},
): boolean | null {
  const [flagEnabled, setFlagEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (
      flagName === 'internal-search' &&
      process.env.NODE_ENV !== 'production' &&
      process.env.NEXT_PUBLIC_KOR_LOCAL_QA_SEARCH === '1' &&
      typeof window !== 'undefined' &&
      window.location.hostname === 'localhost'
    ) {
      setFlagEnabled(true);
      return;
    }

    const checkFeatureFlag = (finishedLoading = false) => {
      if (!isMounted) return;

      const isEnabled = posthog.isFeatureEnabled(flagName);
      if (typeof isEnabled === 'boolean') {
        setFlagEnabled(isEnabled);
      } else if (!waitForLoad || finishedLoading || posthog.featureFlags.hasLoadedFlags) {
        setFlagEnabled(false);
      }
    };

    checkFeatureFlag();

    const onFlagsLoaded = () => checkFeatureFlag(true);
    posthog.featureFlags.addFeatureFlagsHandler(onFlagsLoaded);
    // Analytics must not leave product navigation loading indefinitely when
    // flags are blocked or offline. A later successful response still updates it.
    const timeout = waitForLoad ? setTimeout(() => checkFeatureFlag(true), 3000) : undefined;

    return () => {
      isMounted = false;
      posthog.featureFlags.removeFeatureFlagsHandler(onFlagsLoaded);
      clearTimeout(timeout);
    };
  }, [flagName, waitForLoad]);

  return flagEnabled;
}
